import PropTypes from 'prop-types';
import React, { useRef, useEffect, useState } from 'react';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

export const Dropdown = ({
  value,
  label,
  name,
  selectedOption,
  options = [],
  onChange,
  required = false,
  error,
  disabled = false,
  className = '',
  style = {},
  onBlur,
  multiSelect = false,
  selected = [], // for multi-select: array of selected indices
  placeholder = 'Select an option',
  getLabel = (option) => option.label || option,
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click (for multi-select dropdown)
  useEffect(() => {
    if (!multiSelect) return;
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [multiSelect]);

  const handleSelectAll = () => {
    if (selected.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map((_, idx) => idx));
    }
  };

  const handleToggle = (idx) => {
    if (selected.includes(idx)) {
      onChange(selected.filter((i) => i !== idx));
    } else {
      onChange([...selected, idx]);
    }
  };

  if (multiSelect) {
    // Multi-select dropdown UI
    return (
      <div className={`relative ${className}`} style={style} ref={dropdownRef}>
        {label && (
          <div className="px-1">
            <label htmlFor={name}>
              {label}
              {required && (
                <span className="text-red-600 font-semibold">&nbsp;*</span>
              )}
            </label>
          </div>
        )}
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="w-full bg-gray-100 border border-gray-300 rounded-md p-2 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center"
        >
          <span className="text-gray-700">
            {selected.length > 0 ? `${selected.length} Selected` : placeholder}
          </span>
          <span className="text-gray-500">{open ? '▲' : '▼'}</span>
        </button>
        {open && (
          <div className="absolute w-full bg-white border border-gray-300 rounded-md mt-2 shadow-lg z-10">
            {/* Select All / Deselect All */}
            <div
              className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
              onClick={handleSelectAll}
            >
              {selected.length === options.length ? (
                <FaTimesCircle className="text-red-500 mr-2" />
              ) : (
                <FaCheckCircle className="text-green-500 mr-2" />
              )}
              <span className="text-gray-700 font-medium">
                {selected.length === options.length
                  ? 'Deselect All'
                  : 'Select All'}
              </span>
            </div>
            <hr className="border-gray-200" />
            {/* Options */}
            {options.map((option, idx) => (
              <div
                key={option.value || idx}
                className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => handleToggle(idx)}
              >
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={selected.includes(idx)}
                  readOnly
                />
                <span className="text-gray-700">{getLabel(option)}</span>
              </div>
            ))}
          </div>
        )}
        {error && (
          <small
            style={{
              fontSize: '12px',
              height: '20px',
              display: error ? 'block' : 'none',
            }}
            className="text-red-600 font-semibold text-break px-1 h-4"
          >
            {error}
          </small>
        )}
      </div>
    );
  }

  return (
    <div className={` p-2 font-400 ${className}`} style={{ ...style }}>
      {label && (
        <div className="px-1">
          <label htmlFor={name}>
            {label}
            {required && (
              <span className="text-red-600 font-semibold">&nbsp;*</span>
            )}
          </label>
        </div>
      )}
      <div className="">
        <select
          name={name}
          onChange={onChange}
          disabled={disabled}
          className={`min-w-full border p-2 rounded-lg ${
            disabled ? 'bg-gray-200' : ''
          }`}
          onBlur={onBlur}
          value={value}
        >
          <option value="" disabled selected>
            {selectedOption || 'Select an option'}
          </option>
          {options?.map((option) => (
            <option
              className="min-w-full"
              key={option.value + option.label}
              value={option.value}
            >
              {option?.label}
            </option>
          ))}
        </select>
        {error && (
          <small
            style={{
              fontSize: '12px',
              height: '20px',
              display: error ? 'block' : 'none',
            }}
            className="text-red-600 font-semibold text-break px-1 h-4"
          >
            {error}
          </small>
        )}
      </div>
    </div>
  );
};

Dropdown.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  name: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    }),
  ),
  onChange: PropTypes.func,
  required: PropTypes.bool,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  onBlur: PropTypes.func,
  multiSelect: PropTypes.bool,
  selected: PropTypes.array,
  placeholder: PropTypes.string,
  getLabel: PropTypes.func,
};
